import { Avatar, Box, CircularProgress, Tab, Tabs } from "@mui/material";
import { useMemo, useState } from "react";
import { IGroupUsers, IUserBasic } from "../../types";
import { StyledBadge } from "../styledBadge";
import { contactsListAtom } from "../../atoms/socket";
import { useAtomSelector, useAtomValue } from "@zedux/react";
import { usersByCategorySelector } from "../../atoms/userListAtom";
import { onlineUsersAtom } from "../../atoms/onlineUsersAtom";
import agoCalculator from "../../functions/agoCalculator";

const tabLabels: Record<string, string> = {
    staffs: "کارکنان",
    guests: "مهمانان",
    buyers: "خریداران",
    admins: "مدیران",
    affiliates: "بازاریاب ها"
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
    return (
        <div role="tabpanel" className="w-full" hidden={value !== index}>
            {value === index && <div className="w-full">{children}</div>}
        </div>
    );
}

// ---------- click mode ----------

interface ClickModeProps {
    mode: "click";
    onUserClick: (userId: number) => void;
    loadingUserId?: number;
    userSet?: never;
    onToggle?: never;
    toggle?: never;
}

// ---------- checkbox mode ----------

interface CheckboxModeProps {
    mode: "checkbox";
    userSet: Set<number>;
    onToggle: (userId: number) => void;
    toggle: boolean;          // just to force a re-render from parent
    onUserClick?: never;
    loadingUserId?: never;
}

type UserListPanelProps = {
    filteredName: string;
    excludeIds?: number[];
} & (ClickModeProps | CheckboxModeProps);

export function UserListPanel(props: UserListPanelProps) {
    const { filteredName, mode, excludeIds } = props;
    const [tabValue, setTabValue] = useState(0);

    const usersInGroups = useAtomValue(contactsListAtom);

    // Check which categories have users and create a list of active tab keys
    const activeTabKeys = useMemo(
        () => Object.keys(usersInGroups).filter((key) => Array.isArray((usersInGroups as any)[key]) && (usersInGroups as any)[key].length > 0),
        [usersInGroups]
    );

    const currentTabKey = activeTabKeys[tabValue] ?? "";
    const currentUsers = useAtomSelector(usersByCategorySelector, currentTabKey)
    const onlineUsers = useAtomValue(onlineUsersAtom)

    const filteredUsers = useMemo(() => {
        let users = excludeIds?.length
            ? currentUsers.filter((u) => !excludeIds.includes(u.id))
            : currentUsers;
        if (filteredName) {
            users = users.filter((u) => (u.fullname ?? "").includes(filteredName));
        }
        return users;
    }, [currentUsers, filteredName, excludeIds]);

    // console.log(usersInGroups)
    // console.log("Rendering UserListPanel with users: ", currentUsers, " and online users: ", onlineUsers, " in tab: ", currentTabKey);

    if (activeTabKeys.length === 0) {
        return (
            <span className="block p-4 text-stone-500 text-center">
                لیست کاربران خالی است
            </span>
        );
    }

    function renderUser(u: IUserBasic, i: number) {
        const userId = u.id;
        if (!userId && !u.fullname) return null;

        const isLoadingThis = mode === "click" && props.loadingUserId === userId;
        const isChecked = mode === "checkbox" && userId != null && props.userSet!.has(userId);
        const isUserOnline = onlineUsers.has(userId);

        const inner = (
            <>
                <div className="flex flex-row items-center gap-2">
                    {isLoadingThis ? (
                        <CircularProgress size={32} />
                    ) : (
                        <StyledBadge
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            invisible={!isUserOnline}
                            color="success"
                            variant="dot"
                            overlap="circular"
                        >
                            <Avatar src={u.avatar_url} alt={u.fullname} sx={{ width: 40, height: 40 }}>
                                {u.fullname ? u.fullname[0] : "?"}
                            </Avatar>
                        </StyledBadge>
                    )}
                    <div className="flex flex-col">
                        <span className="font-medium text-sm">{u.fullname}</span>
                        {/* <span className="text-[10px] text-stone-400 capitalize">{u.relation}</span> */}
                    </div>
                </div>
                <div className="flex flex-row items-center gap-2">
                    <span className="text-stone-400 text-xs">
                        {isUserOnline ? (<span className="text-sky-500">آنلاین</span>) : u.last_seen ? (agoCalculator(u.last_seen)) : null}
                    </span>
                    {mode === "checkbox" && (
                        <input
                            type="checkbox"
                            className="w-4 h-4 accent-sky-500"
                            checked={isChecked}
                            onChange={() => {
                                if (userId != null) props.onToggle!(userId);
                            }}
                        />
                    )}
                </div>
            </>
        );

        if (mode === "click") {
            return (
                <div
                    key={userId ?? i}
                    onClick={() => !isLoadingThis && userId != null && props.onUserClick!(userId)}
                    className="flex flex-row justify-between items-center hover:bg-stone-50 px-2 py-2 border-b border-b-stone-100 w-full cursor-pointer"
                >
                    {inner}
                </div>
            );
        }

        return (
            <label
                key={userId ?? i}
                className="flex flex-row justify-between items-center hover:bg-stone-50 px-2 py-2 border-b border-b-stone-100 w-full cursor-pointer"
            >
                {inner}
            </label>
        );
    }

    return (
        <div className="flex flex-col w-full">
            {activeTabKeys.length > 1 && (
                <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_, v) => setTabValue(v)}
                        variant="fullWidth"
                        aria-label="user categories"
                    >
                        {activeTabKeys.map((key, i) => (
                            <Tab key={key} label={tabLabels[key] || key} id={`user-tab-${i}`} />
                        ))}
                    </Tabs>
                </Box>
            )}

            {activeTabKeys.map((key, index) => (
                <TabPanel key={key} value={tabValue} index={index}>
                    {filteredUsers.length === 0 ? (
                        <span className="block p-4 text-stone-500 text-center">
                            هیچ کاربری پیدا نشد
                        </span>
                    ) : (
                        filteredUsers.map((u, i) => renderUser(u, i))
                    )}
                </TabPanel>
            ))}
        </div>
    );
}
